import { useEffect, useMemo, useState } from 'react';

// One shared 60s interval drives all subscribers so a screen with many
// minute-tick-driven rows (e.g. the dock-lane status table) doesn't fan
// out into N timers and N renders per minute.
const tickSubscribers = new Set<() => void>();
let tickerInterval: ReturnType<typeof setInterval> | null = null;
let tickerTimeout: ReturnType<typeof setTimeout> | null = null;

const minuteBucket = (): number => Math.floor(Date.now() / 60_000);

const fireTick = () => {
  for (const cb of tickSubscribers) cb();
};

const ensureTicker = () => {
  if (tickerInterval || tickerTimeout) return;
  // Align the first tick to the next wall-clock minute so `now` doesn't lag the real
  // clock by up to ~60s — matters near midnight in non-UTC zones, where the date key
  // fed into `useLaneShipmentCounts` query args would otherwise stay on yesterday's
  // date until the next unaligned tick fires.
  const msUntilNextMinute = 60_000 - (Date.now() % 60_000);
  tickerTimeout = setTimeout(() => {
    tickerTimeout = null;
    fireTick();
    tickerInterval = setInterval(fireTick, 60_000);
  }, msUntilNextMinute);
};

// Background tabs throttle setInterval, so re-tick on focus to resync `now`.
const onVisibilityChange = () => {
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    fireTick();
  }
};

/**
 * State is a numeric minute bucket so `setState` is a no-op when the same
 * minute fires twice (e.g. visibilitychange while the minute hasn't rolled).
 */
export const useMinuteTick = (): Date => {
  const [bucket, setBucket] = useState(minuteBucket);
  useEffect(() => {
    const tick = () => setBucket(minuteBucket());
    const firstSubscriber = tickSubscribers.size === 0;
    tickSubscribers.add(tick);
    ensureTicker();
    if (firstSubscriber && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
    return () => {
      tickSubscribers.delete(tick);
      if (tickSubscribers.size === 0) {
        if (tickerTimeout) {
          clearTimeout(tickerTimeout);
          tickerTimeout = null;
        }
        if (tickerInterval) {
          clearInterval(tickerInterval);
          tickerInterval = null;
        }
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', onVisibilityChange);
        }
      }
    };
  }, []);
  return useMemo(() => new Date(bucket * 60_000), [bucket]);
};
